import Icon from "@/components/Icon";
import { CreateAlbumModal } from "@/modules/album/components/CreateModal";
import { DeleteAlbumImagesModal } from "@/modules/album/components/DeleteImagesModal";
import DeleteModal from "@/modules/album/components/DeleteModal";
import OperationMenu from "@/modules/album/components/OperationMenu";
import RenameModal from "@/modules/album/components/RenameModal";
import { albumImageOpStaticList, albumOpList } from "@/modules/album/constant";
import { albumStore, IAlbumImageInfo } from "@/modules/album/store";
import { AlbumData } from "@/pages/Gallery/components/Album";
import GalleryDetail from "@/pages/Gallery/components/GalleryDetail";
import { themeStore } from "@/stores/theme";
import { compressedUrl } from "@/utils/string";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { Box, Button, chakra, Flex, Text, useBoolean } from "@chakra-ui/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GalleryList from "react-photo-gallery";
import WaterFlowCard from "./WaterFlowCard";
import { ReactComponent as MoreIcon } from "@/assets/icons/mySpace/more.svg";
import { ReactComponent as FormatIcon } from "@/assets/icons/mySpace/format.svg";
import { ReactComponent as EmptyIcon } from "@/assets/icons/mySpace/empty_album.svg";
import { BoardImageDetailModal } from "@/components/bussiness/BoardImageDetail";
import { AlbumWords } from "@/utils/lang/album";
import { useTranslate } from "@/utils/lang";
import { useAlbumListMenuScroll } from "@/modules/album/hook/useAlbumListMenuScroll";

interface Props {
  onBack: () => void;
}
type GalleryImageDetailType = {
  source: "finished";
  key: number | null;
};
type BoardImageDetailType = {
  source: "board";
  key: string | null;
};
type ImageDetailData = GalleryImageDetailType | BoardImageDetailType;

function WaterFlow(props: Props) {
  const { onBack } = props;
  const { themeColor, boardBg } = themeStore.styles;
  const [modalVisible, modalOp] = useBoolean(false);
  const [modalType, setModal] = useState("rename");
  const [opMode, setOpMode] = useBoolean(false);
  const [imageDetail, imageDetailOp] = useBoolean(false);
  const [imageDetailData, setImageDetailData] = useState<ImageDetailData>({
    source: "finished",
    key: null,
  });

  const { translate } = useTranslate();
  const photos: {
    src: string;
    width: number;
    height: number;
    alt?: string | undefined;
    key?: string | undefined;
  }[] = albumStore.displayImages.map((info) => {
    return {
      width: info.width,
      height: info.height,
      key: info.id + "",
      src: compressedUrl(info.image_url, 500),
    };
  });

  const toggle = (id: number) => {
    if (albumStore.imageBatchOpSet.has(id)) {
      albumStore.imageBatchOpSet.delete(id);
    } else {
      albumStore.imageBatchOpSet.add(id);
    }
  };

  const isToggleAll = albumStore.imageBatchOpSet.size === albumStore.displayImages.length;
  const toggleAll = () => {
    if (isToggleAll) {
      albumStore.clearImageSet();
    } else {
      runInAction(() => {
        albumStore.displayImages.forEach((img) => {
          albumStore.imageBatchOpSet.add(img.id);
        });
      });
    }
  };

  const albumInfo = albumStore.displayAlbumInfo;

  const onOperateTitle = useCallback(
    (type: string) => {
      setModal(type);
      modalOp.on();
    },
    [setModal, modalOp.on],
  );

  const modal = useMemo(() => {
    switch (modalType) {
      case "delete":
        return (
          <DeleteModal
            isOpen={modalVisible}
            onClose={() => {
              modalOp.off();
              albumStore.resetDetail();
              onBack();
            }}
            albumId={albumStore.curAlbumId}
          />
        );
      case "deleteImage":
        return (
          <DeleteAlbumImagesModal
            isOpen={modalVisible}
            onClose={() => {
              modalOp.off();
              setOpMode.off();
            }}
            albumId={albumStore.curAlbumId}
            imageIds={Array.from(albumStore.imageBatchOpSet.values())}
          />
        );
      case "rename":
        return (
          <RenameModal
            isOpen={modalVisible}
            onClose={modalOp.off}
            albumId={albumStore.curAlbumId}
          />
        );
      case "create":
        return <CreateAlbumModal isOpen={modalVisible} onClose={modalOp.off} />;
    }
  }, [modalType, modalVisible, modalOp.off, albumStore.curAlbumId, setOpMode.off, onBack]);

  useEffect(() => {
    if (!opMode) {
      albumStore.clearImageSet();
    }
  }, [opMode]);

  const onMovePictures = (id: string) => {
    if (id === "create") {
      setModal("create");
      modalOp.on();
    } else {
      const fromId = albumStore.curAlbumId,
        toId = +id,
        size = albumStore.imageBatchOpSet.size;
      if (fromId === toId || size === 0) return;
      albumStore
        .movePictures(fromId, toId, Array.from(albumStore.imageBatchOpSet.values()))
        .then(setOpMode.off);
    }
  };

  const onImageDetailShow = useCallback(
    (info: IAlbumImageInfo) => {
      if (!info) return;
      if (info.source === "finished") {
        setImageDetailData({
          source: info.source,
          key: info.image_id,
        } as GalleryImageDetailType);
      }
      if (info.source === "board") {
        setImageDetailData({
          source: info.source,
          key: info.board_alias,
        } as BoardImageDetailType);
      }
      setTimeout(imageDetailOp.on, 0);
    },
    [imageDetailOp.on, setImageDetailData],
  );

  const onAlbum = async (type: number, data: AlbumData) => {
    albumStore.fetchPromptDetail(data.image_id);
    albumStore.refreshAll();
  };

  const refreshImages = async (imageId: number) => {
    albumStore.fetchPromptDetail(imageId);
    albumStore.refreshImages();
  };

  const handleDetailClose = () => {
    imageDetailOp.off();
    setImageDetailData({
      source: "finished",
      key: null,
    });
  };
  const { scrollContainerRef: albumListScrollRef } = useAlbumListMenuScroll();
  return (
    <Box
      css={{
        "&::-webkit-scrollbar": {
          width: "0px",
          opacity: 0,
        },
      }}
    >
      <Flex h="40px" color="white" pos="relative">
        <Flex w="30%" align="center">
          <Flex
            align="center"
            cursor="pointer"
            onClick={() => {
              albumStore.resetDetail();
              onBack();
            }}
          >
            <ChevronLeftIcon fontSize="24px" />
            <Text fontSize="20px" ml="0.5em">
              {translate(AlbumWords.backMyAlbum)}
            </Text>
          </Flex>
        </Flex>
        <Flex w="40%" align="center" justify="center" fontSize="32px">
          <Text
            display="inline-block"
            maxW="90%"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {albumInfo?.name ?? ""}
          </Text>
          {albumInfo?.is_default === 1 ? null : (
            <OperationMenu onSelect={onOperateTitle} menuList={albumOpList}>
              <Icon svg={MoreIcon} style={{ width: "24px", height: "24px", marginLeft: "0.5em" }} />
            </OperationMenu>
          )}
        </Flex>
        <Flex w="30%" align="center" justify="flex-end"></Flex>
      </Flex>
      <Flex h="40px" color="white" mb="8px">
        <Flex grow={1} shrink={0} align="center" justify="flex-end">
          <Flex
            p="6px 12px"
            align="center"
            rounded="8px"
            color="#585858"
            border="1px solid"
            borderColor="currentColor"
            cursor="pointer"
            onClick={setOpMode.toggle}
          >
            <Icon svg={FormatIcon} squared={false} style={{ display: "inline-block" }} />
            {opMode ? translate(AlbumWords.cancelFormat) : translate(AlbumWords.format)}
          </Flex>
        </Flex>
      </Flex>
      {photos.length === 0 ? (
        <Box mt="20%" mx="auto">
          <Icon svg={EmptyIcon} squared={false} style={{ margin: "0 auto" }} />
          <Text color="#b3b3b3" textAlign="center" mt="40px">
            {translate(AlbumWords.emptyAlbum)}
          </Text>
        </Box>
      ) : (
        <GalleryList
          columns={5}
          direction="column"
          photos={photos}
          margin={15}
          renderImage={(data) => {
            const { left, top, photo, index } = data;
            const info: IAlbumImageInfo = albumStore.detail.album_image[index];
            return (
              <WaterFlowCard
                key={photo.key}
                left={left}
                top={top}
                onToggle={toggle}
                id={info.id}
                imageId={info.image_id}
                avatar={info.user_avatar}
                username={info.username}
                src={photo.src}
                opMode={opMode}
                like={info.likes}
                isLike={info.is_like}
                w={photo.width}
                h={photo.height}
                sourceType={info.source}
                onClick={() => onImageDetailShow(info)}
              />
            );
          }}
        />
      )}
      {albumStore.detailPageIdx === albumStore.detailMaxPages ? (
        <Text textAlign="center">{translate(AlbumWords.noMore)}</Text>
      ) : null}
      {modal}
      <Flex
        display={opMode ? "flex" : "none"}
        px="100px"
        pos="fixed"
        bottom="0"
        color="#999999"
        left="0"
        w="100vw"
        h="80px"
        justify="space-between"
        align="center"
        bg={boardBg}
      >
        <Flex>
          <Box>
            {translate(AlbumWords.hasChose)}
            <chakra.span color={themeColor}>{albumStore.imageBatchOpSet.size}</chakra.span>
            {translate(AlbumWords.unit)}
          </Box>
          <Text cursor="pointer" ml="1em" onClick={toggleAll}>
            {isToggleAll ? translate(AlbumWords.cancelToggleAll) : translate(AlbumWords.toggleAll)}
          </Text>
        </Flex>
        <Flex>
          <OperationMenu
            menuList={albumStore.albumListOptions}
            menuListRef={albumListScrollRef as any}
            onSelect={onMovePictures}
            staticList={albumImageOpStaticList}
            variant="spaceMoveImage"
            value={albumStore.detail!.album_id?.toString()}
          >
            <Button
              as="div"
              w="140px"
              mr="14px"
              color="inherit"
              bg="transparent"
              border="1px solid currentColor"
              borderColor="currentColor"
              _hover={{}}
              _active={{}}
            >
              {translate(AlbumWords.changeAlbum)}
            </Button>
          </OperationMenu>
          <Button
            w="140px"
            color="inherit"
            bg="transparent"
            border="1px solid currentColor"
            borderColor="currentColor"
            _hover={{}}
            _active={{}}
            onClick={() => {
              if (albumStore.imageBatchOpSet.size !== 0) {
                setModal("deleteImage");
                modalOp.on();
              }
            }}
          >
            {translate(AlbumWords.deleteImage)}
          </Button>
        </Flex>
      </Flex>
      {imageDetail && imageDetailData.source === "finished" ? (
        <Flex
          pos="fixed"
          w="100%"
          h="100vh"
          left="0"
          top="0"
          bg="rgba(0,0,0,0.5)"
          align="center"
          justify="center"
        >
          <GalleryDetail
            currentID={Number(imageDetailData.key)}
            onAlbum={onAlbum}
            onLike={refreshImages}
            onCancelLike={refreshImages}
            onClose={() => {
              handleDetailClose();
            }}
            store={albumStore}
          />
        </Flex>
      ) : null}
      {imageDetailData.source === "board" && (
        <BoardImageDetailModal
          open={imageDetail}
          onClose={() => {
            handleDetailClose();
          }}
          alias={imageDetailData.key!}
        />
      )}
    </Box>
  );
}

export default observer(WaterFlow);
